import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Wordmark } from "../Wordmark";

const NAV_LINKS = [
  { href: "/#how", label: "How it works", testid: "nav-how" },
  { href: "/#founding", label: "Founding artists", testid: "nav-founding" },
  { href: "/contact", label: "Contact", testid: "nav-contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu whenever the viewport widens past md so the drawer
  // doesn't linger visually when a user rotates or resizes.
  useEffect(() => {
    const close = () => setMobileOpen(false);
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 transition-colors duration-500"
      style={{
        backgroundColor: scrolled ? "rgba(14,10,22,0.82)" : "transparent",
        backdropFilter: scrolled ? "blur(18px)" : "none",
        borderBottom: scrolled ? "1px solid #2A2233" : "1px solid transparent",
      }}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-[72px] flex items-center justify-between">
        <a href="/" data-testid="nav-home-link" className="flex items-center">
          <Wordmark className="h-5" testid="nav-wordmark" />
        </a>

        <nav
          data-testid="nav-desktop-links"
          className="hidden md:flex items-center gap-10 text-xs uppercase tracking-[0.2em]"
          style={{ color: "#9B8FAC" }}
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.testid}
              href={link.href}
              data-testid={link.testid}
              className="hover:text-[#F4EEF7] transition-colors duration-300"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden flex items-center justify-center w-10 h-10 -mr-2 transition-colors duration-300"
          style={{ color: "#F4EEF7" }}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="nav-mobile-drawer"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden"
            style={{
              backgroundColor: "rgba(14,10,22,0.96)",
              backdropFilter: "blur(18px)",
              borderBottom: "1px solid #2A2233",
            }}
          >
            <nav className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.testid}
                  href={link.href}
                  data-testid={`${link.testid}-mobile`}
                  onClick={() => setMobileOpen(false)}
                  className="px-2 py-3 text-sm uppercase tracking-[0.2em] hover:text-[#F4EEF7] transition-colors duration-300"
                  style={{ color: "#9B8FAC" }}
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Nav;
